import { useState } from 'react'
import type { ReactNode } from 'react'
import { ChevronRight, ChevronDown, GridIcon, ListIcon } from '@/components/game/GameIcons'

/**
 * Shared primitives for the grouped, collapsible, grid/list surfaces (Ma flotte,
 * Tableau de bord « En cours »): a persisted grid/list toggle, a controlled
 * collapsible section, and a collapse-state hook driving a "tout replier/déplier"
 * button. Sections are CLOSED by default — you land on category headers + counts.
 */

export type ViewMode = 'grid' | 'list'

/** Persisted grid/list preference, one localStorage key per surface. */
export function useViewMode(storageKey: string): [ViewMode, (v: ViewMode) => void] {
  const [view, setView] = useState<ViewMode>(() => {
    try {
      return localStorage.getItem(storageKey) === 'list' ? 'list' : 'grid'
    } catch {
      return 'grid'
    }
  })
  const update = (v: ViewMode): void => {
    setView(v)
    try {
      localStorage.setItem(storageKey, v)
    } catch {
      /* localStorage unavailable (private mode) — keep the in-memory choice */
    }
  }
  return [view, update]
}

export function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
  const cell = 'grid place-items-center w-8 h-8 rounded-md transition-colors'
  return (
    <div className="flex items-center gap-1 rounded-lg border border-sand-500/20 bg-ocean-900/50 p-1" role="group" aria-label="Affichage">
      <button
        type="button"
        onClick={() => onChange('grid')}
        aria-pressed={view === 'grid'}
        title="Vue blocs"
        className={`${cell} ${view === 'grid' ? 'bg-sand-300/15 text-sand-200' : 'text-ocean-300 hover:text-sand-300'}`}
      >
        <GridIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange('list')}
        aria-pressed={view === 'list'}
        title="Vue liste"
        className={`${cell} ${view === 'list' ? 'bg-sand-300/15 text-sand-200' : 'text-ocean-300 hover:text-sand-300'}`}
      >
        <ListIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

export interface CollapseState {
  isOpen: (key: string) => boolean
  toggle: (key: string) => void
  expandAll: (keys: string[]) => void
  collapseAll: () => void
  openCount: number
}

/** Open/closed state for a set of collapsible sections. Default: all closed. */
export function useCollapse(): CollapseState {
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  return {
    isOpen: (key) => open.has(key),
    toggle: (key) =>
      setOpen((prev) => {
        const next = new Set(prev)
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.add(key)
        }
        return next
      }),
    expandAll: (keys) => setOpen(new Set(keys)),
    collapseAll: () => setOpen(new Set()),
    openCount: open.size,
  }
}

/** "Tout déplier" when something is collapsed, "Tout replier" once all are open. */
export function CollapseAllButton({ keys, collapse }: { keys: string[]; collapse: CollapseState }) {
  if (keys.length <= 1) return null
  const allOpen = collapse.openCount >= keys.length
  return (
    <button
      type="button"
      onClick={() => (allOpen ? collapse.collapseAll() : collapse.expandAll(keys))}
      className="text-xs text-ocean-300 hover:text-sand-300 transition-colors px-2 py-1 rounded-md hover:bg-ocean-900/50"
    >
      {allOpen ? 'Tout replier' : 'Tout déplier'}
    </button>
  )
}

/** A controlled collapsible section (header with chevron + icon + title + count). */
export function CollapsibleGroup({
  title,
  icon,
  count,
  open,
  onToggle,
  children,
}: {
  title: string
  icon?: ReactNode
  count?: number
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <section>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 mb-3 text-left group/sec"
      >
        {open ? <ChevronDown className="w-4 h-4 text-ocean-300" /> : <ChevronRight className="w-4 h-4 text-ocean-300" />}
        {icon}
        <span className="font-display text-lg text-sand-200 group-hover/sec:text-sand-100 transition-colors">{title}</span>
        {count !== undefined && <span className="text-xs text-ocean-300 tabular-nums">({count})</span>}
      </button>
      {open && children}
    </section>
  )
}
